import { useEffect } from 'react';

import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import { Input } from '@iwoca/orion';
import { Text, Radio } from '@iwoca/orion-ui';
import { Label } from '@ui';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useGetPaymentCardData } from './useGetPaymentData';

type PaymentPreference = 'full_amount' | 'minimum_amount' | 'fixed_amount';

export type AddPaymentPreferencesFormData = {
    paymentPreference: PaymentPreference;
    fixedAmount?: string;
};

export const AddPaymentPreferencesForm = ({
    onFormChange
}: {
    onFormChange: (data: AddPaymentPreferencesFormData) => void;
}) => {
    const intl = useIntl();
    const { payment_preference, payment_amount } = useGetPaymentCardData();

    const {
        register,
        watch,
        reset,
        formState: { errors }
    } = useForm<AddPaymentPreferencesFormData>({
        defaultValues: {
            paymentPreference: 'full_amount',
            fixedAmount: undefined
        },
        mode: 'onBlur'
    });

    // Reset form values when payment preference data loads
    useEffect(() => {
        reset({
            paymentPreference:
                (payment_preference as PaymentPreference) || 'full_amount',
            fixedAmount: payment_amount?.toString() || undefined
        });
    }, [payment_preference, payment_amount, reset]);

    const paymentPreference = watch('paymentPreference');
    const fixedAmount = watch('fixedAmount');

    useEffect(() => {
        onFormChange({ paymentPreference, fixedAmount });
    }, [paymentPreference, fixedAmount, onFormChange]);

    return (
        <div>
            <Text className="mb-xl mt-2xl text-2xl font-bold">
                <FormattedMessage
                    id="_.credit_card.onboarding.payment_method.repayment_preferences_title"
                    defaultMessage="Monthly repayment preferences"
                />
            </Text>

            <Radio.Frame className="mb-m items-start">
                <Label htmlFor="payment-full" className="cursor-pointer">
                    <Text className="mb-xs text-m font-med">
                        <FormattedMessage
                            id="_.credit_card.onboarding.payment_method.payment_full_title"
                            defaultMessage="Statement balance"
                        />
                    </Text>
                    <Text className="text-s text-structure-40">
                        <FormattedMessage
                            id="_.credit_card.onboarding.payment_method.payment_full_description"
                            defaultMessage="Settle my balance in full every month."
                        />
                    </Text>
                </Label>
                <Radio
                    id="payment-full"
                    value="full_amount"
                    {...register('paymentPreference')}
                    className="mt-m"
                />
            </Radio.Frame>

            <Radio.Frame className="mb-m items-start">
                <Label htmlFor="payment-minimum" className="cursor-pointer">
                    <Text className="mb-xs text-m font-med">
                        <FormattedMessage
                            id="_.credit_card.onboarding.payment_method.minimum_payment_title"
                            defaultMessage="Minimum payment"
                        />
                    </Text>
                    <Text className="text-s text-primary-10">
                        <FormattedMessage
                            id="_.credit_card.onboarding.payment_method.minimum_payment_description"
                            defaultMessage="Pay the minimum amount due each month. This is either £100 or 10% of your statement balance, whichever is greater. "
                        />
                    </Text>
                    <Text className="mt-m text-xs text-structure-40">
                        <FormattedMessage
                            id="_.credit_card.onboarding.payment_method.minimum_payment_note"
                            defaultMessage="Please note, if your balance at the end of the month is under £100, your balance will be cleared in full."
                        />
                    </Text>
                </Label>
                <Radio
                    id="payment-minimum"
                    value="minimum_amount"
                    {...register('paymentPreference')}
                    className="mt-m"
                />
            </Radio.Frame>

            <Radio.Frame className="mb-m items-start">
                <Label htmlFor="payment-fixed" className="cursor-pointer">
                    <Text className="mb-xs text-m font-med">
                        <FormattedMessage
                            id="_.credit_card.onboarding.payment_method.fixed_amount_title"
                            defaultMessage="Fixed amount"
                        />
                    </Text>
                    <Text className="text-s text-primary-10">
                        <FormattedMessage
                            id="_.credit_card.onboarding.payment_method.fixed_amount_description"
                            defaultMessage="Pay a fixed amount each month. If this amount is higher than the statement balance then the full statement balance will be collected."
                        />
                    </Text>
                </Label>
                <Radio
                    id="payment-fixed"
                    value="fixed_amount"
                    {...register('paymentPreference')}
                    className="mt-m"
                />
            </Radio.Frame>

            {paymentPreference === 'fixed_amount' && (
                <div className="mt-m">
                    <Label htmlFor="fixed-amount">
                        <Text className="mb-xs text-m font-med">
                            <FormattedMessage
                                id="_.credit_card.onboarding.payment_method.fixed_amount_input_label"
                                defaultMessage="How much would you like to pay each month?"
                            />
                        </Text>
                    </Label>
                    <Input
                        id="fixed-amount"
                        type="number"
                        placeholder={intlFormatCurrency({ intl, value: 200 })}
                        {...register('fixedAmount', {
                            required: {
                                value: paymentPreference === 'fixed_amount',
                                message: intl.formatMessage({
                                    id: '_.credit_card.onboarding.payment_method.fixed_amount_required',
                                    defaultMessage:
                                        'Please enter a fixed amount to pay'
                                })
                            },
                            min: {
                                value: 100,
                                message: intl.formatMessage({
                                    id: '_.credit_card.onboarding.payment_method.fixed_amount_min',
                                    defaultMessage:
                                        'Minimum payment amount is £100'
                                })
                            }
                        })}
                        className="my-m w-full"
                    />
                    {errors.fixedAmount && (
                        <Text className="mt-1 text-s text-error">
                            {errors.fixedAmount.message}
                        </Text>
                    )}
                </div>
            )}
        </div>
    );
};
